import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaPills,
  FaHospital,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Wallet', 'Net Banking', 'Insurance'];

const TEMPLATES = [
  { id: 'pharmacy', label: 'Pharmacy Thermal', accent: '#059669', format: [80, 297] },
  { id: 'banner', label: 'Bold Banner', accent: '#dc2626', format: [80, 297] },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: [80, 297] },
  { id: 'hospital', label: 'Hospital A4', accent: '#1d4ed8', format: 'a4' },
  { id: 'invoice', label: 'A4 GST Invoice', accent: '#0f766e', format: 'a4' },
];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const todayDMY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const nowHM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const genBillNo = (prefix) => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${year}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const newPharmaItem = (name = '', qty = 1, price = 0, batch = '', expiry = '', gst = 12) => ({
  id: Math.random().toString(36).slice(2, 9),
  name,
  batch,
  expiry,
  qty,
  price,
  gst,
});

const newHospitalItem = (name = '', qty = 1, price = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  name,
  batch: '',
  expiry: '',
  qty,
  price,
  gst: 0,
});

const MedicalBillGenerator = () => {
  const [facilityType, setFacilityType] = useState('pharmacy'); // pharmacy | hospital
  const [template, setTemplate] = useState('pharmacy');

  const [facility, setFacility] = useState({
    name: 'Apollo Pharmacy',
    address: '12 Brigade Road, Bengaluru 560001',
    phone: '+91 98765 43210',
    email: 'care@apollopharmacy.in',
    gstin: '29ABCDE1234F1Z5',
    licenseNo: 'KA-B-20A/20B-12345', // Drug License for pharmacy / Hospital Reg No
  });

  const [meta, setMeta] = useState({
    billNo: genBillNo('PH'),
    date: todayDMY(),
    time: nowHM(),
    doctor: 'Dr. R. Sharma',
    prescriptionNo: 'RX-87421',
    paymentMode: 'UPI',
  });

  const [patient, setPatient] = useState({
    name: 'Mr. Rohan Mehta',
    age: '34',
    gender: 'Male',
    phone: '+91 90000 11122',
    address: '',
  });

  const [items, setItems] = useState([
    newPharmaItem('Crocin Advance 500mg (Strip of 15)', 2, 38, 'CRX2412', '12/2026', 12),
    newPharmaItem('Azithromycin 500mg (Strip of 5)', 1, 110, 'AZX0625', '06/2026', 12),
    newPharmaItem('Cetrizine 10mg (Strip of 10)', 1, 28, 'CET0825', '08/2026', 12),
    newPharmaItem('Volini Spray 60g', 1, 198, 'VLN1124', '11/2026', 18),
  ]);

  const [tax, setTax] = useState({
    cgst: 6,
    sgst: 6,
    discountPercent: 0,
    roundOff: true,
  });

  const [logo, setLogo] = useState(null);

  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#059669';
  const isPharmacy = facilityType === 'pharmacy';

  // ---------- Switch facility type → swap defaults ----------
  const switchFacility = (type) => {
    setFacilityType(type);
    if (type === 'pharmacy') {
      setTemplate('pharmacy');
      setFacility((f) => ({
        ...f,
        name: f.name && f.name !== 'City Care Hospital' ? f.name : 'Apollo Pharmacy',
      }));
      setMeta((m) => ({ ...m, billNo: genBillNo('PH') }));
      setItems([
        newPharmaItem('Crocin Advance 500mg (Strip of 15)', 2, 38, 'CRX2412', '12/2026', 12),
        newPharmaItem('Azithromycin 500mg (Strip of 5)', 1, 110, 'AZX0625', '06/2026', 12),
      ]);
    } else {
      setTemplate('hospital');
      setFacility((f) => ({
        ...f,
        name: f.name && f.name !== 'Apollo Pharmacy' ? f.name : 'City Care Hospital',
      }));
      setMeta((m) => ({ ...m, billNo: genBillNo('HOS') }));
      setItems([
        newHospitalItem('Consultation - General Physician', 1, 600),
        newHospitalItem('CBC (Complete Blood Count)', 1, 450),
        newHospitalItem('X-Ray Chest PA View', 1, 350),
        newHospitalItem('Room Charges - Semi Private (per day)', 1, 2500),
      ]);
    }
  };

  // ---------- COMPUTATIONS ----------
  const totals = useMemo(() => {
    let subtotal = 0;
    let totalGst = 0;
    items.forEach((it) => {
      const q = Number(it.qty) || 0;
      const p = Number(it.price) || 0;
      const line = q * p;
      subtotal += line;
      const lineGst = (line * (Number(it.gst) || 0)) / 100;
      totalGst += lineGst;
    });
    const discount = (subtotal * (Number(tax.discountPercent) || 0)) / 100;
    const afterDiscount = subtotal - discount;
    // Apply CGST/SGST split — half of (cgst+sgst) configured % on afterDiscount when item gst is zero (hospital)
    const itemGstSum = totalGst;
    let cgst = 0;
    let sgst = 0;
    if (itemGstSum > 0) {
      cgst = itemGstSum / 2;
      sgst = itemGstSum / 2;
    } else {
      cgst = (afterDiscount * (Number(tax.cgst) || 0)) / 100;
      sgst = (afterDiscount * (Number(tax.sgst) || 0)) / 100;
    }
    const grand = afterDiscount + cgst + sgst;
    const grandRounded = Math.round(grand);
    return {
      subtotal,
      discount,
      cgst,
      sgst,
      grand: Math.round(grand * 100) / 100,
      grandRounded,
      roundOff: grandRounded - grand,
    };
  }, [items, tax]);

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const addItem = () =>
    setItems((prev) => [...prev, isPharmacy ? newPharmaItem() : newHospitalItem()]);
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const handleReset = () => {
    setFacility({ name: '', address: '', phone: '', email: '', gstin: '', licenseNo: '' });
    setMeta({
      billNo: genBillNo(isPharmacy ? 'PH' : 'HOS'),
      date: todayDMY(),
      time: nowHM(),
      doctor: '',
      prescriptionNo: '',
      paymentMode: 'Cash',
    });
    setPatient({ name: '', age: '', gender: '', phone: '', address: '' });
    setItems([isPharmacy ? newPharmaItem() : newHospitalItem()]);
    setTax({ cgst: 6, sgst: 6, discountPercent: 0, roundOff: true });
  };

  const finalAmount = tax.roundOff ? totals.grandRounded : totals.grand;

  // ---------- THERMAL HELPERS ----------
  const drawItemsThermal = (doc, startY, w) => {
    let y = startY;
    const left = 5;
    const right = w - 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text('Item', left, y);
    doc.text('Qty', left + 40, y, { align: 'right' });
    doc.text('Rate', left + 56, y, { align: 'right' });
    doc.text('Amt', right, y, { align: 'right' });
    y += 3;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.line(left, y, right, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    items.forEach((it) => {
      const name = it.name || '-';
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const amt = qty * price;
      const nameLines = doc.splitTextToSize(name, 38);
      doc.text(nameLines, left, y);
      doc.text(String(qty), left + 40, y, { align: 'right' });
      doc.text(price.toFixed(2), left + 56, y, { align: 'right' });
      doc.text(amt.toFixed(2), right, y, { align: 'right' });
      let lineH = Math.max(4, nameLines.length * 4);
      if (isPharmacy && (it.batch || it.expiry)) {
        y += lineH;
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(7);
        const sub = [it.batch && `B:${it.batch}`, it.expiry && `Exp:${it.expiry}`]
          .filter(Boolean)
          .join('  ');
        doc.text(sub, left, y);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        lineH = 4;
      }
      y += lineH;
    });
    y += 2;
    doc.line(left, y, right, y);
    return y + 4;
  };

  const drawTotalsThermal = (doc, startY, w, color) => {
    let y = startY;
    const left = 5;
    const right = w - 5;
    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 9);
      doc.setTextColor(0, 0, 0);
      doc.text(label, left, y);
      doc.text(value, right, y, { align: 'right' });
      y += bold ? 6 : 5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0)
      row(`Discount (${tax.discountPercent}%)`, `- ${formatINR(totals.discount)}`);
    if (totals.cgst > 0) row('CGST', formatINR(totals.cgst));
    if (totals.sgst > 0) row('SGST', formatINR(totals.sgst));
    if (tax.roundOff && Math.abs(totals.roundOff) > 0.001)
      row('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(left, y, right, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...color);
    doc.text('NET PAYABLE', left, y);
    doc.text(`Rs. ${formatINR(finalAmount)}`, right, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Payment: ${meta.paymentMode}`, left, y);
    y += 5;
    return y;
  };

  // 1. PHARMACY THERMAL
  const renderPharmacy = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 10;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 16) / 2, 4, 16, facility.name, [240, 240, 240]);
      y = 24;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...color);
    doc.text(facility.name || 'Pharmacy', w / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    if (facility.address) {
      const lines = doc.splitTextToSize(facility.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (facility.phone) { doc.text(`Tel: ${facility.phone}`, w / 2, y, { align: 'center' }); y += 3.5; }
    if (facility.gstin) { doc.text(`GSTIN: ${facility.gstin}`, w / 2, y, { align: 'center' }); y += 3.5; }
    if (facility.licenseNo) { doc.text(`DL No: ${facility.licenseNo}`, w / 2, y, { align: 'center' }); y += 3.5; }

    y += 1;
    doc.setDrawColor(0, 0, 0);
    doc.line(5, y, w - 5, y);
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('CASH / CREDIT MEMO', w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.text(`Bill: ${meta.billNo}`, 5, y);
    doc.text(meta.date, w - 5, y, { align: 'right' });
    y += 4;
    if (meta.prescriptionNo) {
      doc.text(`Rx: ${meta.prescriptionNo}`, 5, y);
      doc.text(meta.time, w - 5, y, { align: 'right' });
      y += 4;
    }
    if (patient.name) {
      doc.text(`Patient: ${patient.name}${patient.age ? ` (${patient.age}${patient.gender ? '/' + patient.gender[0] : ''})` : ''}`, 5, y);
      y += 4;
    }
    if (meta.doctor) { doc.text(`Doctor: ${meta.doctor}`, 5, y); y += 4; }
    y += 1;

    y = drawItemsThermal(doc, y, w);
    y = drawTotalsThermal(doc, y, w, color);

    y += 4;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Drugs once sold cannot be taken back.', w / 2, y, { align: 'center' });
    y += 3.5;
    doc.text('Please retain bill for warranty / insurance claim.', w / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Get Well Soon!', w / 2, y, { align: 'center' });
    return doc;
  };

  // 2. BOLD BANNER
  const renderBanner = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 0;
    const bannerH = logo ? 36 : 24;
    doc.setFillColor(...color);
    doc.rect(0, 0, w, bannerH, 'F');
    if (logo) drawPdfLogo(doc, logo, (w - 14) / 2, 3, 14, facility.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(facility.name || 'Medical', w / 2, logo ? 22 : 10, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(isPharmacy ? 'TAX INVOICE' : 'HOSPITAL BILL', w / 2, logo ? 28 : 16, { align: 'center' });
    if (facility.gstin) doc.text(`GSTIN ${facility.gstin}`, w / 2, logo ? 32 : 20, { align: 'center' });

    y = bannerH + 4;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    if (facility.address) {
      const lines = doc.splitTextToSize(facility.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (facility.phone) { doc.text(`Tel: ${facility.phone}`, w / 2, y, { align: 'center' }); y += 3.5; }
    y += 2;

    doc.setFontSize(9);
    doc.text(`Bill: ${meta.billNo}`, 5, y);
    doc.text(`${meta.date} ${meta.time}`, w - 5, y, { align: 'right' });
    y += 4;
    if (patient.name) {
      doc.text(`Patient: ${patient.name}`, 5, y);
      y += 4;
    }
    if (meta.doctor) {
      doc.text(`Dr: ${meta.doctor}`, 5, y);
      y += 4;
    }
    y += 1;

    y = drawItemsThermal(doc, y, w);

    // Big total
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const tline = (label, value) => {
      doc.text(label, 5, y);
      doc.text(value, w - 5, y, { align: 'right' });
      y += 4;
    };
    tline('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) tline(`Discount (${tax.discountPercent}%)`, `- ${formatINR(totals.discount)}`);
    if (totals.cgst > 0) tline('CGST', formatINR(totals.cgst));
    if (totals.sgst > 0) tline('SGST', formatINR(totals.sgst));
    y += 2;
    doc.setFillColor(...color);
    doc.rect(4, y, w - 8, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('NET PAYABLE', 6, y + 8);
    doc.text(`Rs. ${formatINR(finalAmount)}`, w - 6, y + 8, { align: 'right' });
    y += 16;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Wishing you good health!', w / 2, y, { align: 'center' });
    return doc;
  };

  // 3. MINIMAL MONO THERMAL
  const renderMinimal = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    let y = 12;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 14) / 2, 4, 14, facility.name, [240, 240, 240]);
      y = 22;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text(facility.name || 'Medical', w / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (facility.address) {
      const lines = doc.splitTextToSize(facility.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (facility.phone) { doc.text(facility.phone, w / 2, y, { align: 'center' }); y += 3.5; }
    y += 2;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(5, y, w - 5, y);
    y += 5;

    doc.setFontSize(9);
    doc.text(meta.billNo, 5, y);
    doc.text(`${meta.date} ${meta.time}`, w - 5, y, { align: 'right' });
    y += 4;
    if (patient.name) { doc.text(`Patient: ${patient.name}`, 5, y); y += 4; }
    if (meta.doctor) { doc.text(`Doctor: ${meta.doctor}`, 5, y); y += 4; }
    y += 1;

    y = drawItemsThermal(doc, y, w);
    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 9);
      doc.text(label, 5, y);
      doc.text(value, w - 5, y, { align: 'right' });
      y += bold ? 6 : 4.5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) row('Discount', `- ${formatINR(totals.discount)}`);
    if (totals.cgst > 0) row('CGST', formatINR(totals.cgst));
    if (totals.sgst > 0) row('SGST', formatINR(totals.sgst));
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 5;
    row('TOTAL', `Rs. ${formatINR(finalAmount)}`, true);
    y += 4;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 5;
    doc.text('Get well soon.', w / 2, y, { align: 'center' });
    return doc;
  };

  // 4. HOSPITAL A4 (sidebar style)
  const renderHospital = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210, h = 297;
    const sideW = 55;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, sideW, h, 'F');
    if (logo) drawPdfLogo(doc, logo, (sideW - 28) / 2, 12, 28, facility.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const titleY = logo ? 48 : 22;
    const nameLines = doc.splitTextToSize(facility.name || 'Hospital', sideW - 10);
    nameLines.forEach((ln, i) => doc.text(ln, sideW / 2, titleY + i * 6, { align: 'center' }));
    let sy = titleY + nameLines.length * 6 + 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (facility.address) {
      const lines = doc.splitTextToSize(facility.address, sideW - 10);
      lines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    if (facility.phone) { doc.text(facility.phone, sideW / 2, sy, { align: 'center' }); sy += 3.5; }
    if (facility.email) {
      const eLines = doc.splitTextToSize(facility.email, sideW - 10);
      eLines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    sy += 2;
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.2);
    doc.line(8, sy, sideW - 8, sy);
    sy += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    if (facility.gstin) { doc.text(`GSTIN`, sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); doc.text(facility.gstin, sideW / 2, sy, { align: 'center' }); sy += 5; doc.setFont('helvetica', 'bold'); }
    if (facility.licenseNo) { doc.text(`Reg / DL No`, sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); doc.text(facility.licenseNo, sideW / 2, sy, { align: 'center' }); sy += 4; }

    // Right pane
    const left = sideW + 10;
    const right = w - 14;
    let y = 18;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...color);
    doc.text(isPharmacy ? 'PHARMACY INVOICE' : 'HOSPITAL BILL', left, y);
    y += 8;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.6);
    doc.line(left, y, right, y);
    y += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Patient', left, y);
    doc.text('Bill Details', (left + right) / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(patient.name || '-', left, y);
    doc.text(`Bill No: ${meta.billNo}`, (left + right) / 2, y);
    y += 5;
    const pInfo = [patient.age && `${patient.age} yrs`, patient.gender, patient.phone].filter(Boolean).join('  |  ');
    if (pInfo) { doc.text(pInfo, left, y); }
    doc.text(`Date: ${meta.date}   ${meta.time}`, (left + right) / 2, y);
    y += 5;
    if (patient.address) {
      const addrLines = doc.splitTextToSize(patient.address, (right - left) / 2 - 5);
      addrLines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
    }
    if (meta.doctor) { doc.text(`Doctor: ${meta.doctor}`, (left + right) / 2, y - (patient.address ? 4 : 0)); }
    if (meta.prescriptionNo) {
      doc.text(`Rx: ${meta.prescriptionNo}`, (left + right) / 2, y);
      y += 4;
    }
    y += 6;

    // Items table
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('#', left + 3, y + 6);
    doc.text(isPharmacy ? 'Medicine / Item' : 'Service / Charge', left + 10, y + 6);
    doc.text('Qty', right - 60, y + 6, { align: 'right' });
    doc.text('Rate', right - 38, y + 6, { align: 'right' });
    doc.text('Amount', right - 3, y + 6, { align: 'right' });
    y += 9;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    items.forEach((it, idx) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const amt = qty * price;
      const nameLines = doc.splitTextToSize(it.name || '-', right - left - 90);
      const hasSub = isPharmacy && (it.batch || it.expiry);
      const rowH = Math.max(7, nameLines.length * 4.5 + (hasSub ? 4 : 2));
      doc.rect(left, y, right - left, rowH);
      doc.text(String(idx + 1), left + 3, y + 5);
      doc.text(nameLines, left + 10, y + 5);
      if (hasSub) {
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(8);
        const sub = [it.batch && `Batch: ${it.batch}`, it.expiry && `Exp: ${it.expiry}`, it.gst ? `GST ${it.gst}%` : null]
          .filter(Boolean)
          .join('   ');
        doc.text(sub, left + 10, y + 5 + nameLines.length * 4.5);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
      }
      doc.text(String(qty), right - 60, y + 5, { align: 'right' });
      doc.text(price.toFixed(2), right - 38, y + 5, { align: 'right' });
      doc.text(amt.toFixed(2), right - 3, y + 5, { align: 'right' });
      y += rowH;
    });

    y += 4;
    const totalsX = right - 90;
    const row = (label, value, bold = false, big = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(big ? 13 : 10);
      doc.text(label, totalsX, y);
      doc.text(value, right, y, { align: 'right' });
      y += big ? 8 : 5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) row(`Discount (${tax.discountPercent}%)`, `- ${formatINR(totals.discount)}`);
    if (totals.cgst > 0) row('CGST', formatINR(totals.cgst));
    if (totals.sgst > 0) row('SGST', formatINR(totals.sgst));
    if (tax.roundOff && Math.abs(totals.roundOff) > 0.001)
      row('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(totalsX, y, right, y);
    y += 6;
    doc.setTextColor(...color);
    row('NET PAYABLE', `Rs. ${formatINR(finalAmount)}`, true, true);
    doc.setTextColor(0, 0, 0);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Payment Mode: ${meta.paymentMode}`, left, y);

    y += 18;
    doc.setDrawColor(120, 120, 120);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Authorised Signatory', right - 30, y + 6, { align: 'center' });

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...color);
    doc.text(isPharmacy
      ? 'Drugs once sold cannot be taken back. Please retain bill for insurance claims.'
      : 'This is a computer generated bill. Please retain for insurance / reimbursement.',
      w / 2, 285, { align: 'center' });
    return doc;
  };

  // 5. A4 GST INVOICE
  const renderInvoice = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);
    let y = 0;

    doc.setFillColor(...color);
    doc.rect(0, 0, w, 32, 'F');
    if (logo) drawPdfLogo(doc, logo, left, 4, 22, facility.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(facility.name || 'Medical', logo ? left + 26 : left, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(isPharmacy ? 'TAX INVOICE - PHARMACY' : 'TAX INVOICE - HOSPITAL', logo ? left + 26 : left, 22);
    if (facility.gstin) doc.text(`GSTIN: ${facility.gstin}`, logo ? left + 26 : left, 28);
    doc.setFontSize(9);
    if (facility.address) {
      const addrLines = doc.splitTextToSize(facility.address, 80);
      addrLines.forEach((ln, i) => doc.text(ln, right, 12 + i * 4, { align: 'right' }));
    }
    if (facility.phone) doc.text(`Tel: ${facility.phone}`, right, 24, { align: 'right' });
    if (facility.licenseNo) doc.text(`DL/Reg: ${facility.licenseNo}`, right, 30, { align: 'right' });

    y = 42;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Billed To (Patient)', left, y);
    doc.text('Invoice Details', w / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(patient.name || '-', left, y);
    doc.text(`Bill No: ${meta.billNo}`, w / 2, y);
    y += 5;
    const pInfo = [patient.age && `${patient.age} yrs`, patient.gender, patient.phone].filter(Boolean).join('  |  ');
    if (pInfo) doc.text(pInfo, left, y);
    doc.text(`Date: ${meta.date}   Time: ${meta.time}`, w / 2, y);
    y += 5;
    if (meta.doctor) doc.text(`Doctor: ${meta.doctor}`, w / 2, y);
    if (patient.address) {
      const addrLines = doc.splitTextToSize(patient.address, (w / 2) - left - 5);
      addrLines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
    }
    if (meta.prescriptionNo) { y += 4; doc.text(`Rx No: ${meta.prescriptionNo}`, w / 2, y); }
    y += 8;

    // Items table
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('#', left + 3, y + 6);
    doc.text(isPharmacy ? 'Medicine' : 'Service', left + 10, y + 6);
    if (isPharmacy) {
      doc.text('Batch / Exp', left + 80, y + 6);
    }
    doc.text('Qty', right - 60, y + 6, { align: 'right' });
    doc.text('Rate', right - 38, y + 6, { align: 'right' });
    doc.text('GST%', right - 18, y + 6, { align: 'right' });
    doc.text('Amount', right - 3, y + 6, { align: 'right' });
    y += 9;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    items.forEach((it, idx) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const amt = qty * price;
      const nameLines = doc.splitTextToSize(it.name || '-', isPharmacy ? 65 : (right - left - 90));
      const rowH = Math.max(7, nameLines.length * 4.5 + 2);
      doc.rect(left, y, right - left, rowH);
      doc.text(String(idx + 1), left + 3, y + 5);
      doc.text(nameLines, left + 10, y + 5);
      if (isPharmacy) {
        doc.setFontSize(8);
        if (it.batch) doc.text(`B: ${it.batch}`, left + 80, y + 5);
        if (it.expiry) doc.text(`Exp: ${it.expiry}`, left + 80, y + 9);
        doc.setFontSize(9);
      }
      doc.text(String(qty), right - 60, y + 5, { align: 'right' });
      doc.text(price.toFixed(2), right - 38, y + 5, { align: 'right' });
      doc.text(String(it.gst || 0), right - 18, y + 5, { align: 'right' });
      doc.text(amt.toFixed(2), right - 3, y + 5, { align: 'right' });
      y += rowH;
    });

    y += 4;
    const totalsX = right - 90;
    const row = (label, value, bold = false, big = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(big ? 13 : 10);
      doc.text(label, totalsX, y);
      doc.text(value, right, y, { align: 'right' });
      y += big ? 8 : 5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) row(`Discount (${tax.discountPercent}%)`, `- ${formatINR(totals.discount)}`);
    if (totals.cgst > 0) row('CGST', formatINR(totals.cgst));
    if (totals.sgst > 0) row('SGST', formatINR(totals.sgst));
    if (tax.roundOff && Math.abs(totals.roundOff) > 0.001)
      row('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(totalsX, y, right, y);
    y += 6;
    doc.setTextColor(...color);
    row('NET PAYABLE', `Rs. ${formatINR(finalAmount)}`, true, true);
    doc.setTextColor(0, 0, 0);

    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Payment Mode: ${meta.paymentMode}`, left, y);
    y += 18;
    doc.setDrawColor(120, 120, 120);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Authorised Signatory', right - 30, y + 6, { align: 'center' });

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...color);
    doc.text(isPharmacy
      ? 'Drugs once sold cannot be taken back. Please retain bill for insurance claims.'
      : 'Please retain this bill for insurance / reimbursement purposes.',
      w / 2, 285, { align: 'center' });
    return doc;
  };

  const TEMPLATE_RENDERERS = {
    pharmacy: renderPharmacy,
    banner: renderBanner,
    minimal: renderMinimal,
    hospital: renderHospital,
    invoice: renderInvoice,
  };

  const buildPDF = () => (TEMPLATE_RENDERERS[template] || renderPharmacy)();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`${isPharmacy ? 'Pharmacy' : 'Hospital'}_Bill_${meta.billNo || 'receipt'}.pdf`);
  };
  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Medical Bill Generator (Pharmacy / Hospital)"
        description="Free Medical Bill Generator India - create Pharmacy bills and Hospital invoices with GST (CGST/SGST), batch & expiry, patient details, doctor name and 5 printable templates. Download as PDF."
        keywords="medical bill generator, pharmacy bill format, hospital bill generator, medical invoice india, chemist bill, drug invoice with GST, hospital invoice pdf"
        canonicalPath="/medical-bill"
        calculatorType="MedicalBillGenerator"
        calculatorDescription="Generate Indian pharmacy and hospital bills with itemised medicines/services, GST split, batch/expiry, patient & doctor details, and 5 printable templates."
      />

      <div className="calculator-header">
        <h1>Medical Bill Generator</h1>
        <p>Pharmacy or Hospital bills with GST, batch & expiry, patient details — 5 printable templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Facility Type Toggle */}
          <div className="input-group">
            <label>Facility Type</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              {[
                { v: 'pharmacy', l: 'Pharmacy', Icon: FaPills },
                { v: 'hospital', l: 'Hospital', Icon: FaHospital },
              ].map((opt) => {
                const active = facilityType === opt.v;
                const Ic = opt.Icon;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    className={`btn ${active ? 'btn-primary' : 'btn-outline'}`}
                    style={{ padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => switchFacility(opt.v)}
                  >
                    <Ic /> {opt.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Template */}
          <div className="input-group">
            <label>Bill Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#cbd5e1'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#0f172a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: t.accent,
                        border: active ? '1px solid #fff' : 'none',
                      }}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label={`${isPharmacy ? 'Pharmacy' : 'Hospital'} Logo`} accent={accent} />

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>{isPharmacy ? 'Pharmacy' : 'Hospital'} Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Name</label>
              <input
                type="text"
                value={facility.name}
                onChange={(e) => setFacility({ ...facility, name: e.target.value })}
                placeholder={isPharmacy ? 'e.g. Apollo Pharmacy' : 'e.g. City Care Hospital'}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input
                type="text"
                value={facility.phone}
                onChange={(e) => setFacility({ ...facility, phone: e.target.value })}
                placeholder="+91 ..."
              />
            </div>
          </div>
          <div className="input-group">
            <label>Address</label>
            <input
              type="text"
              value={facility.address}
              onChange={(e) => setFacility({ ...facility, address: e.target.value })}
              placeholder="Street, City, PIN"
            />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Email (optional)</label>
              <input
                type="text"
                value={facility.email}
                onChange={(e) => setFacility({ ...facility, email: e.target.value })}
                placeholder="contact@example.com"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>GSTIN (optional)</label>
              <input
                type="text"
                value={facility.gstin}
                onChange={(e) => setFacility({ ...facility, gstin: e.target.value.toUpperCase() })}
                placeholder="29ABCDE1234F1Z5"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>{isPharmacy ? 'Drug Licence No' : 'Hospital Reg No'}</label>
              <input
                type="text"
                value={facility.licenseNo}
                onChange={(e) => setFacility({ ...facility, licenseNo: e.target.value })}
                placeholder={isPharmacy ? 'KA-B-20A/20B-...' : 'NABH/State Reg'}
              />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Bill Info</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Bill No</label>
              <input
                type="text"
                value={meta.billNo}
                onChange={(e) => setMeta({ ...meta, billNo: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Date</label>
              <input
                type="text"
                value={meta.date}
                onChange={(e) => setMeta({ ...meta, date: e.target.value })}
                placeholder="DD/MM/YYYY"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Time</label>
              <input
                type="text"
                value={meta.time}
                onChange={(e) => setMeta({ ...meta, time: e.target.value })}
                placeholder="HH:MM"
              />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Doctor</label>
              <input
                type="text"
                value={meta.doctor}
                onChange={(e) => setMeta({ ...meta, doctor: e.target.value })}
                placeholder="Dr. Name"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>{isPharmacy ? 'Prescription No' : 'OPD / IPD No'}</label>
              <input
                type="text"
                value={meta.prescriptionNo}
                onChange={(e) => setMeta({ ...meta, prescriptionNo: e.target.value })}
                placeholder={isPharmacy ? 'RX-...' : 'OPD-...'}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Payment Mode</label>
              <select
                value={meta.paymentMode}
                onChange={(e) => setMeta({ ...meta, paymentMode: e.target.value })}
              >
                {PAYMENT_MODES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Patient Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Patient Name</label>
              <input
                type="text"
                value={patient.name}
                onChange={(e) => setPatient({ ...patient, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Age</label>
              <input
                type="text"
                value={patient.age}
                onChange={(e) => setPatient({ ...patient, age: e.target.value })}
                placeholder="34"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Gender</label>
              <select
                value={patient.gender}
                onChange={(e) => setPatient({ ...patient, gender: e.target.value })}
              >
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Patient Phone</label>
              <input
                type="text"
                value={patient.phone}
                onChange={(e) => setPatient({ ...patient, phone: e.target.value })}
                placeholder="+91 ..."
              />
            </div>
            <div className="input-group" style={{ flex: 2 }}>
              <label>Patient Address (optional)</label>
              <input
                type="text"
                value={patient.address}
                onChange={(e) => setPatient({ ...patient, address: e.target.value })}
                placeholder="Street, City"
              />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 8 }}>
            {isPharmacy ? 'Medicines' : 'Services / Charges'}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((it, idx) => (
              <div
                key={it.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: 10,
                  background: '#f8fafc',
                }}
              >
                <div className="flex-row" style={{ alignItems: 'flex-end' }}>
                  <div className="input-group" style={{ flex: 3, marginBottom: 0 }}>
                    <label>{idx + 1}. {isPharmacy ? 'Medicine Name' : 'Service / Description'}</label>
                    <input
                      type="text"
                      value={it.name}
                      onChange={(e) => updateItem(it.id, { name: e.target.value })}
                      placeholder={isPharmacy ? 'e.g. Crocin 500mg' : 'e.g. CBC Test'}
                    />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Qty</label>
                    <input
                      type="number"
                      min="0"
                      value={it.qty}
                      onChange={(e) => updateItem(it.id, { qty: e.target.value })}
                    />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>{isPharmacy ? 'MRP' : 'Rate'}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.price}
                      onChange={(e) => updateItem(it.id, { price: e.target.value })}
                    />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>GST %</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.gst}
                      onChange={(e) => updateItem(it.id, { gst: e.target.value })}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(it.id)}
                    className="btn btn-outline"
                    style={{ padding: '8px 10px', marginBottom: 0 }}
                    title="Remove"
                  >
                    <FaTrash />
                  </button>
                </div>
                {isPharmacy && (
                  <div className="flex-row" style={{ marginTop: 8 }}>
                    <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label>Batch No</label>
                      <input
                        type="text"
                        value={it.batch}
                        onChange={(e) => updateItem(it.id, { batch: e.target.value })}
                        placeholder="e.g. CRX2412"
                      />
                    </div>
                    <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label>Expiry (MM/YYYY)</label>
                      <input
                        type="text"
                        value={it.expiry}
                        onChange={(e) => updateItem(it.id, { expiry: e.target.value })}
                        placeholder="12/2026"
                      />
                    </div>
                    <div style={{ flex: 2 }} />
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: '#475569', textAlign: 'right' }}>
                  Line Total: <strong>₹ {formatINR((Number(it.qty) || 0) * (Number(it.price) || 0))}</strong>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addItem}
            className="btn btn-outline"
            style={{ marginTop: 10 }}
          >
            <FaPlus /> Add {isPharmacy ? 'Medicine' : 'Service'}
          </button>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Tax & Discount</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>CGST % (fallback)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={tax.cgst}
                onChange={(e) => setTax({ ...tax, cgst: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>SGST % (fallback)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={tax.sgst}
                onChange={(e) => setTax({ ...tax, sgst: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Discount %</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={tax.discountPercent}
                onChange={(e) => setTax({ ...tax, discountPercent: e.target.value })}
              />
            </div>
          </div>
          <div className="input-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={tax.roundOff}
                onChange={(e) => setTax({ ...tax, roundOff: e.target.checked })}
              />
              Round off net payable to nearest rupee
            </label>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              If any medicine has its own GST %, item-wise GST is used. Otherwise CGST+SGST fallback applies.
            </div>
          </div>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button type="button" onClick={handleDownloadPDF} className="btn btn-primary">
              <FaDownload /> Download PDF
            </button>
            <button type="button" onClick={handlePrint} className="btn btn-outline">
              <FaPrint /> Print
            </button>
            <button type="button" onClick={handleReset} className="btn btn-outline">
              <FaRedo /> Reset
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>
            Live preview · Template: <strong>{TEMPLATES.find((t) => t.id === template)?.label}</strong>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
            }}
          >
            <BillPreview
              template={template}
              accent={accent}
              facility={facility}
              patient={patient}
              meta={meta}
              items={items}
              totals={totals}
              tax={tax}
              isPharmacy={isPharmacy}
              finalAmount={finalAmount}
              logo={logo}
            />
          </div>
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: 8,
              fontSize: 13,
              color: '#065f46',
            }}
          >
            <strong>Net Payable:</strong> ₹ {formatINR(finalAmount)}
            {totals.cgst + totals.sgst > 0 && (
              <> · GST: ₹ {formatINR(totals.cgst + totals.sgst)}</>
            )}
            {totals.discount > 0 && <> · Discount: ₹ {formatINR(totals.discount)}</>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- LIVE PREVIEW ----------
const previewBase = {
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  color: '#0f172a',
};

const LogoImg = ({ logo, style }) =>
  logo ? <img src={logo} alt="logo" style={{ objectFit: 'contain', ...style }} /> : null;

const BillPreview = ({ template, accent, facility, patient, meta, items, totals, tax, isPharmacy, finalAmount, logo }) => {
  const formatINRp = (n) =>
    Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const ItemsTable = ({ compact }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 11 : 12 }}>
      <thead>
        <tr style={{ background: '#f1f5f9' }}>
          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>
            {isPharmacy ? 'Medicine' : 'Service'}
          </th>
          {!compact && isPharmacy && (
            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Batch / Exp</th>
          )}
          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Qty</th>
          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Rate</th>
          {!compact && (
            <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>GST%</th>
          )}
          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Amt</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => {
          const qty = Number(it.qty) || 0;
          const price = Number(it.price) || 0;
          return (
            <tr key={it.id} style={{ borderBottom: '1px dashed #e2e8f0' }}>
              <td style={{ padding: '6px 8px' }}>{it.name || '—'}</td>
              {!compact && isPharmacy && (
                <td style={{ padding: '6px 8px', fontSize: 11, color: '#64748b' }}>
                  {it.batch ? `B: ${it.batch}` : ''}
                  {it.batch && it.expiry ? ' · ' : ''}
                  {it.expiry ? `Exp ${it.expiry}` : ''}
                </td>
              )}
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{qty}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatINRp(price)}</td>
              {!compact && (
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{it.gst || 0}%</td>
              )}
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatINRp(qty * price)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const TotalsBlock = ({ alignRight = true }) => (
    <div style={{ marginLeft: alignRight ? 'auto' : 0, marginTop: 12, minWidth: 240, fontSize: 13 }}>
      <Row label="Subtotal" value={formatINRp(totals.subtotal)} />
      {totals.discount > 0 && (
        <Row label={`Discount (${tax.discountPercent}%)`} value={`- ${formatINRp(totals.discount)}`} />
      )}
      {totals.cgst > 0 && <Row label="CGST" value={formatINRp(totals.cgst)} />}
      {totals.sgst > 0 && <Row label="SGST" value={formatINRp(totals.sgst)} />}
      {tax.roundOff && Math.abs(totals.roundOff) > 0.001 && (
        <Row
          label="Round Off"
          value={totals.roundOff >= 0 ? `+ ${formatINRp(totals.roundOff)}` : `- ${formatINRp(Math.abs(totals.roundOff))}`}
        />
      )}
      <div style={{ borderTop: `2px solid ${accent}`, marginTop: 6, paddingTop: 6 }}>
        <Row
          label={<span style={{ color: accent, fontWeight: 800 }}>NET PAYABLE</span>}
          value={<span style={{ color: accent, fontWeight: 800 }}>₹ {formatINRp(finalAmount)}</span>}
        />
      </div>
    </div>
  );

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

  // ---- 1. PHARMACY THERMAL ----
  if (template === 'pharmacy') {
    return (
      <div style={{ ...previewBase, padding: 14, maxWidth: 320, margin: '12px auto', border: '1px dashed #cbd5e1' }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <LogoImg logo={logo} style={{ height: 46 }} />
          </div>
        )}
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 16, color: accent }}>
          {facility.name || 'Pharmacy'}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>
          {facility.address}
          {facility.phone && <><br />Tel: {facility.phone}</>}
          {facility.gstin && <><br />GSTIN: {facility.gstin}</>}
          {facility.licenseNo && <><br />DL: {facility.licenseNo}</>}
        </div>
        <div style={{ borderTop: '1px dashed #94a3b8', margin: '10px 0' }} />
        <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700 }}>CASH / CREDIT MEMO</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11 }}>
          <span>Bill: <strong>{meta.billNo}</strong></span>
          <span>{meta.date}</span>
        </div>
        {meta.prescriptionNo && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
            <span>Rx: {meta.prescriptionNo}</span>
            <span>{meta.time}</span>
          </div>
        )}
        {patient.name && (
          <div style={{ fontSize: 11, marginTop: 2 }}>
            Patient: <strong>{patient.name}</strong>
            {patient.age && <> ({patient.age}{patient.gender ? '/' + patient.gender[0] : ''})</>}
          </div>
        )}
        {meta.doctor && <div style={{ fontSize: 11 }}>Doctor: <strong>{meta.doctor}</strong></div>}
        <div style={{ marginTop: 8 }}>
          <ItemsTable compact />
        </div>
        <TotalsBlock />
        <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: '#64748b' }}>
          Paid via <strong>{meta.paymentMode}</strong>
        </div>
        <div style={{ marginTop: 8, textAlign: 'center', fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
          Drugs once sold cannot be taken back.
        </div>
        <div style={{ marginTop: 4, textAlign: 'center', fontWeight: 700, color: accent }}>
          Get Well Soon!
        </div>
      </div>
    );
  }

  // ---- 2. BANNER ----
  if (template === 'banner') {
    return (
      <div style={{ ...previewBase, maxWidth: 320, margin: '12px auto', border: '1px solid #e2e8f0' }}>
        <div style={{ background: accent, color: '#fff', padding: 14, textAlign: 'center' }}>
          {logo && (
            <div style={{ background: '#fff', display: 'inline-block', padding: 3, borderRadius: 6, marginBottom: 6 }}>
              <LogoImg logo={logo} style={{ height: 40 }} />
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 17 }}>{facility.name || 'Medical'}</div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>
            {isPharmacy ? 'TAX INVOICE' : 'HOSPITAL BILL'}
            {facility.gstin && <> · GSTIN {facility.gstin}</>}
          </div>
        </div>
        <div style={{ padding: 14, fontSize: 11, color: '#475569', textAlign: 'center' }}>
          {facility.address}{facility.phone ? ` · Tel: ${facility.phone}` : ''}
        </div>
        <div style={{ padding: '0 14px', display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
          <span>Bill: <strong>{meta.billNo}</strong></span>
          <span>{meta.date} {meta.time}</span>
        </div>
        {(patient.name || meta.doctor) && (
          <div style={{ padding: '4px 14px 8px', fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
            <span>Patient: <strong>{patient.name || '—'}</strong></span>
            {meta.doctor && <span>Dr: {meta.doctor}</span>}
          </div>
        )}
        <div style={{ padding: '0 14px' }}>
          <ItemsTable compact />
        </div>
        <div style={{ padding: '0 14px' }}>
          <TotalsBlock />
        </div>
        <div style={{ margin: '10px 14px 0', background: accent, color: '#fff', padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          <span>NET PAYABLE</span>
          <span>₹ {formatINRp(finalAmount)}</span>
        </div>
        <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: '#64748b' }}>
          Paid via <strong>{meta.paymentMode}</strong>
        </div>
      </div>
    );
  }

  // ---- 3. MINIMAL ----
  if (template === 'minimal') {
    return (
      <div style={{ ...previewBase, padding: 16, maxWidth: 320, margin: '12px auto', border: '1px solid #cbd5e1' }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <LogoImg logo={logo} style={{ height: 40 }} />
          </div>
        )}
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 15 }}>{facility.name || 'Medical'}</div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>
          {facility.address}{facility.phone ? ` · ${facility.phone}` : ''}
        </div>
        <div style={{ borderTop: '2px solid #0f172a', margin: '10px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
          <span>{meta.billNo}</span>
          <span>{meta.date} {meta.time}</span>
        </div>
        {patient.name && <div style={{ fontSize: 11, marginTop: 4 }}>Patient: {patient.name}</div>}
        {meta.doctor && <div style={{ fontSize: 11 }}>Doctor: {meta.doctor}</div>}
        <div style={{ marginTop: 8 }}><ItemsTable compact /></div>
        <TotalsBlock />
        <div style={{ marginTop: 8, fontSize: 10, fontStyle: 'italic', color: '#94a3b8', textAlign: 'center' }}>
          Paid via {meta.paymentMode} · Get well soon.
        </div>
      </div>
    );
  }

  // ---- 4. HOSPITAL A4 ----
  if (template === 'hospital') {
    return (
      <div style={{ ...previewBase, display: 'flex', minHeight: 440 }}>
        <div style={{ background: accent, color: '#fff', padding: 16, width: 160, flexShrink: 0 }}>
          {logo && (
            <div style={{ background: '#fff', borderRadius: 6, padding: 4, textAlign: 'center', marginBottom: 10 }}>
              <LogoImg logo={logo} style={{ height: 56 }} />
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{facility.name || 'Hospital'}</div>
          <div style={{ fontSize: 10, opacity: 0.9, marginTop: 6, lineHeight: 1.4 }}>
            {facility.address}
            {facility.phone && <><br />{facility.phone}</>}
            {facility.email && <><br />{facility.email}</>}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.4)', margin: '10px 0' }} />
          {facility.gstin && (
            <div style={{ fontSize: 10 }}>
              <div style={{ opacity: 0.85, fontWeight: 700 }}>GSTIN</div>
              <div>{facility.gstin}</div>
            </div>
          )}
          {facility.licenseNo && (
            <div style={{ fontSize: 10, marginTop: 6 }}>
              <div style={{ opacity: 0.85, fontWeight: 700 }}>Reg / DL</div>
              <div>{facility.licenseNo}</div>
            </div>
          )}
        </div>
        <div style={{ flex: 1, padding: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: accent }}>
            {isPharmacy ? 'PHARMACY INVOICE' : 'HOSPITAL BILL'}
          </div>
          <div style={{ borderBottom: `2px solid ${accent}`, margin: '8px 0 14px' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
            <div>
              <div style={{ fontWeight: 700 }}>Patient</div>
              <div>{patient.name || '—'}</div>
              <div style={{ color: '#64748b', fontSize: 11 }}>
                {[patient.age && `${patient.age} yrs`, patient.gender, patient.phone].filter(Boolean).join(' | ')}
              </div>
              {patient.address && <div style={{ fontSize: 11 }}>{patient.address}</div>}
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Bill Details</div>
              <div>Bill No: {meta.billNo}</div>
              <div>Date: {meta.date} {meta.time}</div>
              {meta.doctor && <div>Doctor: {meta.doctor}</div>}
              {meta.prescriptionNo && <div>Rx: {meta.prescriptionNo}</div>}
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <ItemsTable />
          </div>
          <TotalsBlock />
          <div style={{ marginTop: 10, fontSize: 12 }}>
            Payment Mode: <strong>{meta.paymentMode}</strong>
          </div>
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 160, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
              Authorised Signatory
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- 5. A4 GST INVOICE ----
  return (
    <div style={{ ...previewBase, padding: 0 }}>
      <div style={{ background: accent, color: '#fff', padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        {logo && (
          <div style={{ background: '#fff', borderRadius: 6, padding: 4 }}>
            <LogoImg logo={logo} style={{ height: 56, width: 56 }} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{facility.name || 'Medical'}</div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>
            {isPharmacy ? 'TAX INVOICE - PHARMACY' : 'TAX INVOICE - HOSPITAL'}
          </div>
          {facility.gstin && <div style={{ fontSize: 11 }}>GSTIN: {facility.gstin}</div>}
        </div>
        <div style={{ textAlign: 'right', fontSize: 11 }}>
          {facility.address && <div>{facility.address}</div>}
          {facility.phone && <div>Tel: {facility.phone}</div>}
          {facility.licenseNo && <div>DL/Reg: {facility.licenseNo}</div>}
        </div>
      </div>
      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Billed To (Patient)</div>
          <div>{patient.name || '—'}</div>
          <div style={{ color: '#64748b', fontSize: 11 }}>
            {[patient.age && `${patient.age} yrs`, patient.gender, patient.phone].filter(Boolean).join(' | ')}
          </div>
          {patient.address && <div style={{ fontSize: 11 }}>{patient.address}</div>}
        </div>
        <div>
          <div style={{ fontWeight: 700 }}>Invoice Details</div>
          <div>Bill No: {meta.billNo}</div>
          <div>Date: {meta.date} {meta.time}</div>
          {meta.doctor && <div>Doctor: {meta.doctor}</div>}
          {meta.prescriptionNo && <div>Rx: {meta.prescriptionNo}</div>}
        </div>
      </div>
      <div style={{ padding: '0 16px' }}>
        <ItemsTable />
      </div>
      <div style={{ padding: '0 16px 16px' }}>
        <TotalsBlock />
        <div style={{ marginTop: 8, fontSize: 12 }}>
          Payment Mode: <strong>{meta.paymentMode}</strong>
        </div>
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 160, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
            Authorised Signatory
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: 10, fontStyle: 'italic', color: accent, textAlign: 'center' }}>
          {isPharmacy
            ? 'Drugs once sold cannot be taken back. Please retain bill for insurance claims.'
            : 'Please retain this bill for insurance / reimbursement purposes.'}
        </div>
      </div>
    </div>
  );
};

export default MedicalBillGenerator;
